import * as React from "react";
import { useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui";
import { FormResponse } from "@/services/bedrock";
import { formRequestCampaign, formRequestOrigin, routeFanLabel, routePlayerLabel } from "../formRequestSource";

interface Row {
  label: string;
  value: React.ReactNode;
}

function FormLinkRow({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <span className="flex items-center gap-1.5">
      <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
        Open form link
      </a>
      <Button
        variant="ghost"
        size="icon-sm"
        className="size-6 text-slate-400 hover:text-slate-700"
        aria-label={copied ? "Copied" : "Copy form link"}
        onClick={copy}
      >
        {copied ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
      </Button>
    </span>
  );
}

/**
 * "About this response": where its form link came from and how a route matched it. Links made in
 * SocialCoach have nothing to explain, so the section only shows for routes, the API or a campaign.
 */
export function ResponseSourceDetails({ response }: { response: FormResponse }) {
  const link = response.formRequestSource;
  const campaign = link ? formRequestCampaign(link) : null;
  const external = link?.sourceCategory === "route" || link?.sourceCategory === "api";
  if (!link || (!external && !campaign)) return null;

  const rows: Row[] = [
    { label: "Came from", value: [formRequestOrigin(link), link.internalIdentifier].filter(Boolean).join(" · ") },
  ];

  if (campaign) {
    const utm = link.attribution?.utm ?? {};
    const extra = [utm.source, utm.medium].filter(Boolean).join(" · ");
    rows.push({
      label: "Campaign",
      value: (
        <>
          {campaign}
          {extra && <span className="text-slate-400"> ({extra})</span>}
        </>
      ),
    });
  }

  // How the route matched only exists for links a route created
  const route = link.sourceCategory === "route" ? link.attribution?.route : undefined;
  const player = routePlayerLabel(route?.player, response.player?.fullName || null);
  if (player) rows.push({ label: "Player", value: player });
  const fan = routeFanLabel(route?.fan);
  if (fan) rows.push({ label: "Fan", value: fan });

  if (link.url) rows.push({ label: "Form link", value: <FormLinkRow url={link.url} /> });

  return (
    <section className="rounded-xl bg-slate-50 px-4 py-3 space-y-2" data-testid="response-source-details">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 select-none">About this response</h3>
      <dl className="grid grid-cols-[minmax(0,6.5rem)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-xs font-sans">
        {rows.map(row => (
          <React.Fragment key={row.label}>
            <dt className="text-slate-500 select-none">{row.label}</dt>
            <dd className="text-slate-700 min-w-0 break-words">{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}
